import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

import axios from 'axios';
import { useSnackbar } from 'notistack';

import { useAuth } from './AuthContext';
import apiClient from '../services/apiClient';
import voiceService, { VoiceCommand, VoiceSettings, VoiceState } from '../services/voiceService';
import whisperService from '../services/whisperService';
import { refreshToken as refreshAccessToken } from '../utils/auth';
import { getApiBaseUrl } from '../config/runtimeUrls';
import {
  triggerAIAssistantSubmission,
  extractQuestionFromCommand,
  shouldTriggerAI,
  formatVoiceResponse,
  processVoiceCommand as processVoiceCommandUtil,
  generateVoiceFeedback,
  extractActionData,
  isActionCommand,
} from '../utils/voiceUtils';

interface VoiceContextType {
  isListening: boolean;
  isSupported: boolean;
  settings: VoiceSettings;
  toggleListening: () => Promise<void>;
  speak: (text: string) => Promise<void>;
  stopSpeaking: () => void;
  processCommand: (command: string) => Promise<void>;
  updateSettings: (newSettings: Partial<VoiceSettings>) => void;
  addCommandToHistory: (command: string, response: string) => void;
  commandHistory: {
    id: string;
    command: string;
    response: string;
    confidence: number;
    timestamp: Date;
    processed: boolean;
  }[];
  // Additional properties for compatibility
  parsedCommands: any[];
  clearHistory: () => void;
  getStatusText: () => string;
  getStatusColor: () => string;
  voiceService: any;
  isSpeaking: boolean;
  isContinuousMode: boolean;
  conversationHistory: string[];
  startContinuousConversation: () => void;
  stopContinuousConversation: () => void;
  speakText: (text: string) => Promise<void>;
}

const VoiceContext = createContext<VoiceContextType | undefined>(undefined);

export const VoiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [settings, setSettings] = useState<VoiceSettings>(voiceService.getSettings());
  const [isSpeaking, setIsSpeaking] = useState(false);
  const commandHistoryRef = useRef<VoiceContextType['commandHistory']>([]);
  const { enqueueSnackbar } = useSnackbar();
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isContinuousMode, setIsContinuousMode] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<string[]>([]);

  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    const checkSupport = async () => {
      try {
        console.log('🎤 VoiceContext: Checking voice support...');

        // Check MediaRecorder support
        const mediaRecorderSupported = !!window.MediaRecorder;
        console.log('🎤 VoiceContext: MediaRecorder supported:', mediaRecorderSupported);

        // Check microphone access only when authenticated and not loading
        if (!isAuthenticated || isLoading) {
          console.log('🎤 VoiceContext: Skipping mic check (auth not stable)');
          setIsSupported(mediaRecorderSupported);
          return;
        }

        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(track => track.stop());
          console.log('🎤 VoiceContext: Microphone access granted');
          setIsSupported(mediaRecorderSupported);
        } catch (err: any) {
          // ✅ ENTERPRISE FIX 4: Enhanced microphone permission handling
          if (err && err.name === 'NotAllowedError') {
            console.warn('🎤 VoiceContext: Microphone permission denied by user.');
            console.warn(
              '🎤 VoiceContext: To enable voice features, grant microphone access in browser settings.'
            );
            console.warn(
              '🎤 VoiceContext: Chrome: Settings → Privacy and security → Site Settings → Microphone'
            );
            console.warn(
              '🎤 VoiceContext: Edge: Settings → Cookies and site permissions → Microphone'
            );
            // Still mark as supported (MediaRecorder exists) but without mic access
            setIsSupported(mediaRecorderSupported);
          } else {
            throw err;
          }
        }

        console.log('🎤 VoiceContext: Voice support confirmed');
      } catch (error) {
        console.error('🎤 VoiceContext: Voice support check failed:', error);
        setIsSupported(false);
        enqueueSnackbar('Voice features not supported in this browser', { variant: 'warning' });
      }
    };

    checkSupport();
  }, [enqueueSnackbar, isAuthenticated, isLoading]);

  useEffect(() => {
    const handleSettingsChange = (newSettings: VoiceSettings) => {
      console.log('🎤 VoiceContext: Settings updated:', newSettings);
      setSettings(newSettings);
    };

    voiceService.on('settingsChange', handleSettingsChange);
    return () => {
      voiceService.off('settingsChange', handleSettingsChange);
    };
  }, []);

  // Listen for automatic transcription completion
  useEffect(() => {
    const handleTranscriptionComplete = async (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('🎤 VoiceContext: Received automatic transcription:', customEvent.detail);
      const { transcription, audioBlob } = customEvent.detail;

      if (transcription?.text) {
        console.log('🎤 VoiceContext: Processing automatic transcription:', transcription.text);
        await processVoiceCommand(transcription.text);
      } else {
        console.warn('🎤 VoiceContext: No transcription text received');
        await speakText("I didn't understand that. Please try again.");
      }
    };

    window.addEventListener('whisper-transcription-complete', handleTranscriptionComplete);
    return () => {
      window.removeEventListener('whisper-transcription-complete', handleTranscriptionComplete);
    };
  }, []);

  const toggleListening = async () => {
    console.log('🎤 VoiceContext: 🔄 Toggle listening called. Current state:', { isListening });
    console.log('🎤 VoiceContext: 🔧 Current settings:', settings);

    if (isListening) {
      console.log('🎤 VoiceContext: 🛑 Stopping listening...');
      await stopListening();
    } else {
      console.log('🎤 VoiceContext: ▶️ Starting listening...');
      await startListening();
    }
  };

  const startListening = async () => {
    try {
      console.log('🎤 VoiceContext: ▶️ Configuring WhisperService for automatic processing...');

      // Configure WhisperService with voice settings for automatic processing
      whisperService.configure({
        silenceTimeout: 2500, // 2.5 seconds of silence for faster response
        maxRecordingTime: 30000, // 30 seconds max
        chunkInterval: 1000, // 1 second chunks
        autoTranscribe: true,
      });

      // Set up automatic stop callback for seamless processing
      whisperService.setAutoStopCallback(async (audioBlob: Blob) => {
        console.log(
          '🎤 VoiceContext: Auto-stop callback triggered with audio blob, size:',
          audioBlob.size
        );

        if (audioBlob && audioBlob.size > 0) {
          // Proactively refresh token to avoid 401 churn
          try {
            await refreshAccessToken();
          } catch (_) {}
          // Provide immediate voice feedback using OpenAI TTS only
          await speakText('Processing your request...');

          try {
            // Send to Whisper for transcription
            console.log('🎤 VoiceContext: Auto-stop: Sending audio to Whisper...');
            const transcription = await whisperService.transcribeAudio(audioBlob);
            console.log('🎤 VoiceContext: Auto-stop: Transcription received:', transcription);

            if (transcription?.text) {
              try {
                const lang = transcription.language || 'auto';
                window.dispatchEvent(
                  new CustomEvent('whisper-language-detected', { detail: { language: lang } })
                );
              } catch (_) {}
              // Process the command automatically
              await processVoiceCommand(transcription.text);
            } else {
              await speakText("I didn't understand that. Please try again.");
            }
          } catch (error) {
            console.error('🎤 VoiceContext: Auto-stop: Error processing transcription:', error);
            await speakText('Sorry, there was an error processing your request.');
          }
        } else {
          console.warn('🎤 VoiceContext: Auto-stop: No audio data captured');
          await speakText("I didn't hear anything. Please try again.");
        }
      });

      // Start recording
      await whisperService.startRecording();
      setIsListening(true);
      console.log('🎤 VoiceContext: ▶️ Recording started with automatic processing');
    } catch (error) {
      console.error('🎤 VoiceContext: ❌ Error starting listening:', error);
      await speakText('Sorry, there was an error starting voice recognition.');
    }
  };

  const stopListening = async () => {
    if (!isListening) return;

    console.log('🎤 VoiceContext: 🛑 Stopping listening...');
    setIsListening(false);

    try {
      // Stop recording and get audio blob
      const audioBlob = await whisperService.stopRecording();
      console.log('🎤 VoiceContext: Audio blob received, size:', audioBlob?.size || 0);

      if (!audioBlob || audioBlob.size === 0) {
        console.warn('🎤 VoiceContext: No audio data captured');
        await speakText("I didn't hear anything. Please try again.");
        return;
      }

      // Proactively refresh token to avoid 401 churn
      try {
        await refreshAccessToken();
      } catch (_) {}
      // Provide immediate voice feedback
      await speakText('Processing your request...');

      // Send to Whisper for transcription
      console.log('🎤 VoiceContext: Sending audio to Whisper...');
      const transcription = await whisperService.transcribeAudio(audioBlob);
      console.log('🎤 VoiceContext: Transcription received:', transcription);

      if (transcription?.text) {
        try {
          const lang = transcription.language || 'auto';
          window.dispatchEvent(
            new CustomEvent('whisper-language-detected', { detail: { language: lang } })
          );
        } catch (_) {}
        // Process the command
        await processVoiceCommand(transcription.text);
      } else {
        await speakText("I didn't understand that. Please try again.");
      }
    } catch (error: any) {
      console.error('🎤 VoiceContext: Error in stopListening:', error);
      const msg =
        typeof error?.message === 'string'
          ? error.message
          : 'There was an error processing your request.';
      enqueueSnackbar(`Whisper error: ${msg}`, { variant: 'error' });
      await speakText('Sorry, there was an error processing your request.');
    }
  };

  const speakText = async (text: string) => {
    if (!settings.voiceFeedback) return;

    try {
      console.log('🎤 VoiceContext: Speaking text via OpenAI TTS:', text);

      // Stop any currently playing audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }

      // Proactively refresh token for TTS
      try {
        await refreshAccessToken();
      } catch (_) {}
      // Use the correct backend URL instead of relative path
      const base = getApiBaseUrl().replace(/\/api\/?$/, '');
      const ttsResp = await apiClient.post<{ success: boolean; audioUrl?: string; error?: string }>(
        '/tts/speak',
        { text, voice: 'alloy' },
        { withCredentials: true }
      );

      if (ttsResp && (ttsResp as any).audioUrl) {
        // Construct the full URL for the audio file
        const fullAudioUrl = `${base}${(ttsResp as any).audioUrl}`;
        console.log('🎤 VoiceContext: Full audio URL:', fullAudioUrl);
        const audio = new Audio();
        (audio as any).crossOrigin = 'anonymous';
        audio.src = fullAudioUrl;
        audio.preload = 'auto';
        currentAudioRef.current = audio;
        setIsSpeaking(true);

        audio.onended = () => {
          setIsSpeaking(false);
          currentAudioRef.current = null;

          // If in continuous mode, automatically start listening for follow-up
          if (isContinuousMode && !isListening) {
            console.log('🎤 VoiceContext: AI response finished, starting listening for follow-up');
            setTimeout(() => {
              speakText("I'm listening for your next question.");
              setTimeout(() => {
                toggleListening();
              }, 1000);
            }, 500);
          }
        };

        audio.onerror = error => {
          console.error('🎤 VoiceContext: OpenAI TTS audio error:', error);
          setIsSpeaking(false);
          currentAudioRef.current = null;
          enqueueSnackbar('Voice playback failed. Showing text reply.', { variant: 'warning' });
        };

        await audio.play();
        console.log('🎤 VoiceContext: OpenAI TTS audio playing successfully');
      } else {
        console.error('🎤 VoiceContext: OpenAI TTS response missing audioUrl');
        enqueueSnackbar('Audio unavailable. Displaying text reply.', { variant: 'info' });
      }
    } catch (error) {
      console.error('🎤 VoiceContext: OpenAI TTS error:', error);
      enqueueSnackbar('Text-to-speech failed. Showing text reply.', { variant: 'error' });
      setIsSpeaking(false);
      if (currentAudioRef.current) {
        currentAudioRef.current = null;
      }
    }
  };

  const processVoiceCommand = async (command: string) => {
    console.log('🎤 VoiceContext: 🎯 Processing command:', command);

    try {
      // Use enhanced command processing
      const result = processVoiceCommandUtil(command);
      console.log('🎤 VoiceContext: Command analysis result:', result);

      // Add to command history
      addCommandToHistory(command, `Processed as ${result.type} command`);

      // Execute based on command type
      switch (result.type) {
        case 'ai':
          if (result.data.question) {
            await speakText(`Processing your question about ${result.data.question}.`);
            // Trigger AI Assistant with automatic submission
            triggerAIAssistantSubmission(result.data.question);
            await speakText(`I've sent your question to the AI Assistant and it's processing now.`);
          } else {
            await speakText("I've opened the AI Assistant for you.");
            window.dispatchEvent(
              new CustomEvent('voice-navigate', { detail: { section: 'ai-assistant' } })
            );
          }
          break;

        case 'navigation':
          if (result.data.section) {
            window.dispatchEvent(
              new CustomEvent('voice-navigate', { detail: { section: result.data.section } })
            );
            await speakText(generateVoiceFeedback('navigation', result.data));
          } else {
            await speakText("I didn't understand where you want to navigate. Please try again.");
          }
          break;

        case 'action':
          await handleActionCommand(result.data);
          await speakText(generateVoiceFeedback('action', result.data));
          break;

        case 'continuous_start':
          startContinuousConversation();
          break;

        case 'continuous_stop':
          stopContinuousConversation();
          break;

        case 'email':
          await speakText('Email functionality is coming soon.');
          break;

        case 'search':
          await speakText('Search functionality is coming soon.');
          break;

        default:
          await speakText("I didn't understand that command. Please try again.");
          console.log('🎤 VoiceContext: Unknown command type:', result.type);
      }
    } catch (error) {
      console.error('🎤 VoiceContext: Error processing command:', error);
      await speakText('Sorry, there was an error processing your command.');
    }
  };

  // New function to handle action commands
  const handleActionCommand = async (actionData: any) => {
    console.log('🎤 VoiceContext: 🎯 Handling action command:', actionData);

    const { action, target, details } = actionData;

    // Navigate to the target section first
    if (target) {
      window.dispatchEvent(new CustomEvent('voice-navigate', { detail: { section: target } }));
    }

    // Execute the specific action
    switch (action) {
      case 'compose_email':
        // Dispatch email compose event with any extracted data
        window.dispatchEvent(
          new CustomEvent('email-compose', {
            detail: {
              recipient: details?.recipient,
              subject: details?.subject,
              content: details?.content,
            },
          })
        );
        await speakText('Opening email composer for you.');
        break;

      case 'create_case':
        // Dispatch new case event
        window.dispatchEvent(
          new CustomEvent('case-create', {
            detail: { source: 'voice-command' },
          })
        );
        await speakText('Opening the new case form.');
        break;

      case 'create_memo':
      case 'create_document':
      case 'create_contract':
        // Dispatch document creation event
        window.dispatchEvent(
          new CustomEvent('document-create', {
            detail: {
              type: action.replace('create_', ''),
              source: 'voice-command',
            },
          })
        );
        await speakText(
          `Opening ${action.replace('create_', '')} creation in the Documents section.`
        );
        break;

      case 'schedule_meeting':
        // Dispatch calendar event creation
        window.dispatchEvent(
          new CustomEvent('calendar-create', {
            detail: {
              type: 'meeting',
              source: 'voice-command',
            },
          })
        );
        await speakText('Opening calendar to schedule your meeting.');
        break;

      case 'add_client':
        // Dispatch client creation event
        window.dispatchEvent(
          new CustomEvent('client-create', {
            detail: { source: 'voice-command' },
          })
        );
        await speakText('Opening the new client form.');
        break;

      case 'create_task':
        // Dispatch task creation event
        window.dispatchEvent(
          new CustomEvent('task-create', {
            detail: { source: 'voice-command' },
          })
        );
        await speakText('Opening task creation in the Tasks section.');
        break;

      case 'set_reminder':
        // Dispatch reminder creation event
        window.dispatchEvent(
          new CustomEvent('calendar-create', {
            detail: {
              type: 'reminder',
              source: 'voice-command',
            },
          })
        );
        await speakText('Opening calendar to set your reminder.');
        break;

      default:
        await speakText("I didn't understand that action. Please try again.");
        console.log('🎤 VoiceContext: Unknown action:', action);
    }
  };

  const speak = async (text: string) => {
    await speakText(formatVoiceResponse(text));
  };

  const stopSpeaking = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
  };

  const processCommand = async (command: string) => {
    await processVoiceCommand(command);
  };

  const updateSettings = (newSettings: Partial<VoiceSettings>) => {
    voiceService.updateSettings(newSettings);
  };

  const addCommandToHistory = (command: string, response: string) => {
    const newCommand = {
      id: Date.now().toString(),
      command,
      response,
      confidence: 0.8,
      timestamp: new Date(),
      processed: true,
    };

    commandHistoryRef.current = [...commandHistoryRef.current, newCommand];

    // Keep only last 50 commands
    if (commandHistoryRef.current.length > 50) {
      commandHistoryRef.current = commandHistoryRef.current.slice(-50);
    }
  };

  const clearHistory = () => {
    commandHistoryRef.current = [];
  };

  const getStatusText = () => {
    if (isSpeaking) return 'Speaking...';
    if (isListening) return 'Listening...';
    if (isSupported) return 'Ready';
    return 'Not Supported';
  };

  const getStatusColor = () => {
    if (isSpeaking) return '#ff9800';
    if (isListening) return '#4caf50';
    if (isSupported) return '#2196f3';
    return '#f44336';
  };

  // Function to start continuous conversation mode
  const startContinuousConversation = () => {
    setIsContinuousMode(true);
    console.log('🎤 VoiceContext: Starting continuous conversation mode');
    speakText(
      "I'm now in continuous conversation mode. You can ask follow-up questions naturally."
    );
  };

  // Function to stop continuous conversation mode
  const stopContinuousConversation = () => {
    setIsContinuousMode(false);
    setConversationHistory([]);
    console.log('🎤 VoiceContext: Stopping continuous conversation mode');
    speakText('Exiting continuous conversation mode.');
  };

  const value: VoiceContextType = {
    isListening,
    isSpeaking,
    isSupported,
    settings,
    commandHistory: commandHistoryRef.current,
    toggleListening,
    speak,
    stopSpeaking,
    processCommand,
    speakText,
    updateSettings,
    addCommandToHistory,
    clearHistory,
    startContinuousConversation,
    stopContinuousConversation,
    isContinuousMode,
    conversationHistory,
    parsedCommands: [],
    getStatusText,
    getStatusColor,
    voiceService,
  };

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
};

export const useVoice = () => {
  const context = useContext(VoiceContext);
  if (context === undefined) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
};
